import React, {useMemo} from 'react';

import {Typography, styled, useTheme} from '@mui/material';
import {
  Box,
  Divider,
  Stack,
} from '@zoom/react-ui';
import {useTranslation} from 'react-i18next';

import {strEq} from 'Utils';
import {
  isTeamAppt,
} from 'Utils/apptAvailabilityUtils';
import {CONFLICT_CODE, CONFLICT_DESC_MAP, CONFLICT_LABEL_MAP} from 'Utils/consts';

const ListItem = styled('li')(({theme}) => ({
  'marginTop': '8px',
  'marginLeft': '8px',
  '::marker': {
    fontSize: '.75rem',
    color: '#555B62',
  },
}));

const GroupUsers = (props) => {
  const {attendees, schedulerEmails} = props;
  return (
    schedulerEmails.map((schedulerEmail) =>
      <ListItem key={schedulerEmail}>
        <Typography component="span" sx={{fontWeight: 400, fontSize: '.875rem', color: '#555B62', ml: -0.5}}>
          {attendees.find((att) => strEq(schedulerEmail, att.email))?.displayName}
        </Typography>
      </ListItem>
    )
  );
};

/**
 *
 * @param {Spot} spot
 * @param {Appt} appt
 * @return {Object}
 */
export const groupUsersByConflict = (spot, appt) => {
  const conflicts = spot?.conflicts || [];
  const excludedUserSet = new Set(spot?.teamPresenceInfo?.excludedUsers);

  const overbookedUsers = conflicts.find((conflict) => conflict.code === CONFLICT_CODE.UNFAIR)?.users ?? [];
  const bufferConflictUsers = conflicts.find((conflict) => conflict.code === CONFLICT_CODE.BUFFER)?.users ?? [];

  const unavailableUsers = [];
  const userEmails = (appt?.attendees ?? []).filter((att) => att.host).map((att) => (att.email));
  userEmails.forEach((userEmail) => {
    if (excludedUserSet.has(userEmail)) {
      if (!overbookedUsers.includes(userEmail)) {
        unavailableUsers.push(userEmail);
      }
    }
  });

  return {
    bufferConflictUsers,
    calendarConflictUsers: conflicts.find((conflict) => conflict.code === CONFLICT_CODE.CALENDAR)?.users ?? [],
    calendarUnavailableHostExists: !!conflicts.find((conflict) => conflict.code === CONFLICT_CODE.HOST) ||
      !!excludedUserSet.size,
    overbookedUsers: overbookedUsers,
    unavailableUsers: unavailableUsers,
  };
};

/**
 * @param {*} props
 * @return {React.ReactElement}
 */
export function ConflictInfo({appt, spot}) {
  const {t} = useTranslation();
  const conflicts = spot?.conflicts || [];
  const attendees = useMemo(() => (appt?.attendees ?? []).filter((att) => att.host), [appt]);

  const groupedUsers = useMemo(() => {
    return groupUsersByConflict(spot, appt);
  }, [spot, appt]);

  if (conflicts?.length === 0) {
    return undefined;
  }
  return (
    <Box sx={{textAlign: 'start'}}>
      <Stack sx={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '2px',
      }}>
        {conflicts?.map((conflict) => {
          const title = CONFLICT_LABEL_MAP[conflict.code];
          const desc = CONFLICT_DESC_MAP[conflict.code];
          return <div>
            <span style={{fontWeight: 700}}>{title? t(title): conflict.code}</span>
            {desc? ` - ${t(desc)}`: ''}
          </div>;
        })}
      </Stack>
      {isTeamAppt(appt) && (
        !!groupedUsers.calendarConflictUsers?.length || groupedUsers.calendarUnavailableHostExists ||
        !!groupedUsers.bufferConflictUsers?.length
      ) &&
        <>
          <Divider sx={{my: 2}} />

          {!!groupedUsers.calendarConflictUsers.length &&
            <ConflictTag conflict={{code: CONFLICT_CODE.CALENDAR}} sx={{mt: 2}} />}
          <GroupUsers attendees={attendees} schedulerEmails={groupedUsers.calendarConflictUsers} />

          {!!groupedUsers.unavailableUsers.length &&
            <ConflictTag conflict={{code: CONFLICT_CODE.UNAVAILABLE}} sx={{mt: 1}} />}
          <GroupUsers attendees={attendees} schedulerEmails={groupedUsers.unavailableUsers} />

          {!!groupedUsers.overbookedUsers.length &&
            <ConflictTag conflict={{code: CONFLICT_CODE.OVERBOOKED}} sx={{mt: 1}} />}
          <GroupUsers attendees={attendees} schedulerEmails={groupedUsers.overbookedUsers} />

          {!!groupedUsers.bufferConflictUsers.length &&
            <ConflictTag conflict={{code: CONFLICT_CODE.BUFFER}} sx={{mt: 1}} />}
          <GroupUsers attendees={attendees} schedulerEmails={groupedUsers.bufferConflictUsers} />
        </>}
    </Box>
  );
}/**
 *
 * @param {*} props
 * @return {React.ReactElement}
 */
export function ConflictTag(props) {
  const {conflict, sx} = props;
  const {t} = useTranslation();
  const theme = useTheme();

  const tagName = CONFLICT_LABEL_MAP[conflict.code];

  return <Box
    sx={{
      width: 'fit-content',
      padding: '4px 6px 4px 6px',
      height: '20px',
      borderRadius: '9999px',
      // eslint-disable-next-line max-len
      border: `1px solid var(--contextual-color-tokens-badge-border-light-mode-disable, ${theme.palette.common.swatch[30]})`,
      backgroundColor: theme.palette.common.swatch[10],
      ...sx,
    }}
  >
    <Typography
      component="div"
      sx={{
        'fontWeight': 600,
        'lineHeight': '.75rem',
        'fontSize': '10px',
        'color': '#555B62',
        'textTransform': 'uppercase',
        'letterSpacing': '0.065em',
      }}>
      {tagName? t(tagName): conflict.code}
    </Typography>
  </Box>;
}

